import React, { useRef, useEffect, useState } from 'react';
import i18n from 'i18n-js';
import { BaseModal } from 'components/UI';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import clx from 'clsx';
import { TextField } from 'components/FormFields';
import { FETCH_STATE } from 'constants';
import { partnersActions } from 'state/modules/partners';
import { userActions } from 'state/modules/user';
import { Grid, Button } from '@material-ui/core';
import { useStyles } from './Common.styled';
import { PhoneField } from 'components/FormFields';
import Box from '@material-ui/core/Box';

const partnerInviteSchema = () =>
  Yup.object().shape({
    meta_data: Yup.object().shape({
      org_name: Yup.string()
        .min(
          2,
          i18n.t('common.custom_min_length_value', {
            min_value: new Intl.NumberFormat().format(2),
          })
        )
        .required(i18n.t('common.required_field')),
      contact_email: Yup.string()
        .email(i18n.t('common.invalid_email'))
        .required(i18n.t('common.required_field')),
      admin_email: Yup.string()
        .email(i18n.t('common.invalid_email'))
        .required(i18n.t('common.required_field')),
      terms_url: Yup.string().required(i18n.t('common.required_field')),
      support_web_url: Yup.string().required(i18n.t('common.required_field')),
      faqs_url: Yup.string().required(i18n.t('common.required_field')),
      support_sms: Yup.string()
        .min(4, i18n.t('common.required_field')) //Account for the country prefix
        .required(i18n.t('common.required_field')),
      support_email: Yup.string()
        .email(i18n.t('common.invalid_email'))
        .required(i18n.t('common.required_field')),
      contact_phone: Yup.string()
        .min(4, i18n.t('common.required_field')) //Account for the country prefix
        .required(i18n.t('common.required_field')),
    }),
  });

const initialValues = () => ({
  partnership_type: 'BUSINESS',
  meta_data: {
    org_name: '',
    contact_email: '',
    admin_email: '',
    terms_url: '',
    support_web_url: '',
    faqs_url: '',
    support_sms: '',
    support_email: '',
    contact_phone: '',
  },
});

const InviteFFBPartnerModal = ({ open, handleCancel }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const invitePartner = useSelector((state) => state.partners.invitePartner);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const validationSchema = partnerInviteSchema();
  const classes = useStyles();

  const loading = invitePartner === FETCH_STATE.LOADING;

  const handleSubmit = (values) => {
    dispatch(partnersActions.invitePartner(values));
  };

  const onSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (invitePartner === FETCH_STATE.DONE) {
      setShowConfirmation(true);
    }
  }, [invitePartner]); // eslint-disable-line

  const handleConfirm = () => {
    handleClose();
  };

  const handleClose = () => {
    handleCancel();
    setShowConfirmation(false);
    dispatch(partnersActions.resetPartner());
  };

  return (
    <BaseModal
      size="lg"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClose}
      enableCloseButton={false}
      handleClick={handleClose}
      enterButtonLabel={i18n.t('partners.invite')}
      loading={loading}
      containerClassName={classes.inviteContainer}
      hasFooter={!showConfirmation}
      hasHeader={!showConfirmation}
      maxWidth={showConfirmation ? '500px' : '812px'}
    >
      <Grid container alignItems="center">
        <Box width="100%">
          {!showConfirmation ? (
            <Formik
              innerRef={formRef}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              initialValues={initialValues()}
              validationSchema={validationSchema}
            >
              {({ handleSubmit }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={0} style={{ justifyContent: 'center' }}>
                    <Grid item xs={12} sm={12} style={{ textAlign: 'center' }}>
                      <h1 className={classes.modalTitle}>{i18n.t('partners.invite_ffb_title')}</h1>
                    </Grid>
                    <Grid
                      item
                      sm={12}
                      style={{
                        width: '40%',
                        marginBottom: '1.4rem',
                        justifyContent: 'center',
                        alignItems: 'center',
                        justifyItems: 'center',
                      }}
                    >
                      <p className={classes.modalSubtitle}>
                        {i18n.t('partners.invite_description')}
                      </p>
                      <p className={clx(classes.modalSubtitle, classes.mandatory)}>
                        {i18n.t('partners.secondary_description')}
                      </p>
                    </Grid>
                    <Grid container spacing={9} style={{ justifyContent: 'space-around' }}>
                      <Grid item sm={6} style={{ marginBottom: '0.75rem' }}>
                        <Grid style={{ marginBottom: '1.25rem' }} item sm={12}>
                          <h3>{i18n.t('partners.company_info')}</h3>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.org_name"
                            component={TextField}
                            label={i18n.t('partners.org_name')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.contact_email"
                            component={TextField}
                            label={i18n.t('partners.business_email')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.contact_phone"
                            component={PhoneField}
                            label={i18n.t('partners.contact_phone')}
                            required
                            dialcodename="phone_code"
                            disableDropdown={false}
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.admin_email"
                            component={TextField}
                            label={i18n.t('partners.admin_email')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.terms_url"
                            component={TextField}
                            label={i18n.t('partners.terms_url')}
                            required
                          />
                        </Grid>
                      </Grid>
                      <Grid item sm={6} style={{ marginBottom: '0.75rem' }}>
                        <Grid style={{ marginBottom: '1.25rem' }} item sm={12}>
                          <h3>{i18n.t('partners.customer_sup_info')}</h3>
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.faqs_url"
                            component={TextField}
                            label={i18n.t('partners.faqs_url')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.support_web_url"
                            component={TextField}
                            label={i18n.t('partners.support_web_url')}
                            required
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.support_sms"
                            component={PhoneField}
                            label={i18n.t('partners.support_sms_phone')}
                            required
                            dialcodename="phone_code"
                            disableDropdown={false}
                          />
                        </Grid>
                        <Grid item xs={12} style={{ marginBottom: '0.75rem' }}>
                          <Field
                            margin="none"
                            name="meta_data.support_email"
                            component={TextField}
                            label={i18n.t('partners.support_email')}
                            required
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          ) : (
            <div className={clx(classes.confirmContainer, classes.confirmTop)}>
              <h1
                className={classes.confirmTitle}
                dangerouslySetInnerHTML={{
                  __html: i18n.t('partners.confirmation_ffb_title', {
                    org: formRef.current?.values?.meta_data?.org_name,
                  }),
                }}
              ></h1>
              <span
                className={classes.confirmDescription}
                dangerouslySetInnerHTML={{
                  __html: i18n.t('partners.confirmation_ffb_description'),
                }}
              />
              <Button
                variant="contained"
                color="primary"
                className={classes.confirmButton}
                onClick={handleConfirm}
              >
                {i18n.t('common.ok')}
              </Button>
            </div>
          )}
        </Box>
      </Grid>
    </BaseModal>
  );
};

export { InviteFFBPartnerModal };
